import { useEffect, useMemo, useState } from 'react';
import type { Tenant } from '@orqforge/shared';
import {
  apiClient,
  type CostOverview,
  type CostSummaryItem,
  type PaginatedRunCosts,
  type TimeseriesPoint,
} from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';

type DatePreset = 'today' | '7d' | '30d' | 'month' | 'all';

const selectClass =
  'h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

const COLORS = ['#3b82f6', '#a855f7', '#ec4899', '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4'];

function errText(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback;
  try {
    const parsed = JSON.parse(err.message) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.map(String).join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

function fmtCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtDuration(ms: number): string {
  if (ms <= 0) return '0s';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function isoDate(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function buildFilters(preset: DatePreset, tenantId: string, customFrom: string, customTo: string): Record<string, string> {
  const params: Record<string, string> = {};
  if (tenantId) params.tenantId = tenantId;
  const now = new Date();
  switch (preset) {
    case 'today': {
      const d = isoDate(now);
      params.from = d;
      params.to = d;
      break;
    }
    case '7d': {
      const from = new Date(now);
      from.setUTCDate(from.getUTCDate() - 7);
      params.from = isoDate(from);
      break;
    }
    case '30d': {
      const from = new Date(now);
      from.setUTCDate(from.getUTCDate() - 30);
      params.from = isoDate(from);
      break;
    }
    case 'month':
      params.from = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01`;
      break;
    case 'all':
      if (customFrom) params.from = customFrom;
      if (customTo) params.to = customTo;
      break;
    default: {
      const unreachable: never = preset;
      return unreachable;
    }
  }
  return params;
}

function formatWhen(value: string, timezone: string, dateFormat: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const y = get('year');
  const m = get('month');
  const d = get('day');
  const datePart = dateFormat === 'MM/DD/YYYY' ? `${m}/${d}/${y}` : dateFormat === 'DD/MM/YYYY' ? `${d}/${m}/${y}` : `${y}-${m}-${d}`;
  return `${datePart} ${get('hour')}:${get('minute')}`;
}

function BarChart({ points }: { points: TimeseriesPoint[] }) {
  const max = Math.max(1, ...points.map((p) => p.tokensIn + p.tokensOut));
  return (
    <div className="flex h-[300px] items-end gap-1 overflow-x-auto" id="costs-daily-chart">
      {points.length === 0 ? <p className="text-sm text-gray-500">No usage in this range.</p> : null}
      {points.map((p) => (
        <div key={p.period} className="flex h-full min-w-[28px] flex-1 flex-col justify-end" title={`${p.period}: ${p.tokensIn + p.tokensOut} tokens, $${fmtCents(p.costCents)}`}>
          <div className="flex flex-col justify-end" style={{ height: `${((p.tokensIn + p.tokensOut) / max) * 100}%` }}>
            <div className="bg-green-500/70" style={{ height: `${(p.tokensOut / Math.max(1, p.tokensIn + p.tokensOut)) * 100}%` }} />
            <div className="bg-blue-500/70" style={{ height: `${(p.tokensIn / Math.max(1, p.tokensIn + p.tokensOut)) * 100}%` }} />
          </div>
          <div className="truncate text-center text-[10px] text-gray-500">{p.period.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

function RunsChart({ points }: { points: TimeseriesPoint[] }) {
  const max = Math.max(1, ...points.map((p) => p.runCount));
  return (
    <div className="flex h-[300px] items-end gap-1 overflow-x-auto" id="costs-runs-chart">
      {points.length === 0 ? <p className="text-sm text-gray-500">No runs in this range.</p> : null}
      {points.map((p) => (
        <div key={p.period} className="flex h-full min-w-[28px] flex-1 flex-col justify-end" title={`${p.period}: ${p.runCount} runs, ${fmtDuration(p.totalElapsedMs)}`}>
          <div className="rounded-sm bg-blue-500/60" style={{ height: `${(p.runCount / max) * 100}%` }} />
          <div className="truncate text-center text-[10px] text-gray-500">{p.period.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

function ShareList({ title, rows }: { title: string; rows: { name: string; value: number; detail: string }[] }) {
  const total = rows.reduce((sum, row) => sum + row.value, 0) || 1;
  return (
    <div>
      <p className="mb-2 text-xs text-gray-500">{title}</p>
      {rows.length === 0 ? <p className="text-sm text-gray-500">No tenant usage in this range.</p> : null}
      <ul className="space-y-2">
        {rows.map((row, i) => (
          <li key={row.name}>
            <div className="flex justify-between text-xs">
              <span>{row.name}</span>
              <span>{row.detail}</span>
            </div>
            <div className="mt-1 h-2 rounded bg-gray-100 dark:bg-gray-800">
              <div className="h-2 rounded" style={{ width: `${(row.value / total) * 100}%`, background: COLORS[i % COLORS.length] }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CostsPage() {
  const { tenants: shellTenants } = useApp();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<CostOverview | null>(null);
  const [summary, setSummary] = useState<CostSummaryItem[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>(shellTenants);
  const [timeseries, setTimeseries] = useState<TimeseriesPoint[]>([]);
  const [runs, setRuns] = useState<PaginatedRunCosts>({ items: [], total: 0, page: 1, pageSize: 25 });
  const [filterTenantId, setFilterTenantId] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [page, setPage] = useState(1);
  const [timezone, setTimezone] = useState('UTC');
  const [dateFormat, setDateFormat] = useState('YYYY-MM-DD');

  const todayTokensIn = timeseries.reduce((s, p) => s + p.tokensIn, 0);
  const todayTokensOut = timeseries.reduce((s, p) => s + p.tokensOut, 0);
  const todayElapsedMs = timeseries.reduce((s, p) => s + p.totalElapsedMs, 0);
  const mtdTokensIn = summary.reduce((s, p) => s + p.tokensIn, 0);
  const mtdTokensOut = summary.reduce((s, p) => s + p.tokensOut, 0);
  const totalPages = Math.max(1, Math.ceil(runs.total / runs.pageSize));

  const tenantTokens = useMemo(() => {
    const byTenant = new Map<string, { tokensIn: number; tokensOut: number; costCents: number }>();
    for (const item of summary) {
      const existing = byTenant.get(item.tenantName) ?? { tokensIn: 0, tokensOut: 0, costCents: 0 };
      existing.tokensIn += item.tokensIn;
      existing.tokensOut += item.tokensOut;
      existing.costCents += item.costCents;
      byTenant.set(item.tenantName, existing);
    }
    return [...byTenant.entries()]
      .sort((a, b) => b[1].tokensIn + b[1].tokensOut - (a[1].tokensIn + a[1].tokensOut))
      .slice(0, 8)
      .map(([name, v]) => ({
        name,
        value: v.tokensIn + v.tokensOut,
        detail: `${(v.tokensIn + v.tokensOut).toLocaleString()} tokens · $${fmtCents(v.costCents)}`,
      }));
  }, [summary]);

  const tenantRuns = useMemo(() => {
    const byTenant = new Map<string, number>();
    for (const item of summary) byTenant.set(item.tenantName, (byTenant.get(item.tenantName) ?? 0) + item.runCount);
    return [...byTenant.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({ name, value: count, detail: `${count} runs` }));
  }, [summary]);

  async function loadFiltered(nextPage: number, preset: DatePreset, tenantFilter: string, from: string, to: string) {
    const params = buildFilters(preset, tenantFilter, from, to);
    const [series, sums, runPage] = await Promise.all([
      apiClient.getCostTimeseries({ ...params, granularity: 'day' }),
      apiClient.getCostSummary(params),
      apiClient.getCostRuns({ ...params, page: String(nextPage), pageSize: '25' }),
    ]);
    setTimeseries(series);
    setSummary(sums);
    setRuns(runPage);
  }

  useEffect(() => {
    setTenants(shellTenants);
  }, [shellTenants]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const [overviewResult, settings] = await Promise.all([
          apiClient.getCostOverview(),
          apiClient.getSettings().catch(() => null),
        ]);
        if (cancelled) return;
        setOverview(overviewResult);
        if (settings) {
          setTimezone(settings.timezone || 'UTC');
          setDateFormat(settings.dateFormat || 'YYYY-MM-DD');
        }
        await loadFiltered(1, '30d', '', '', '');
        if (!cancelled) setError(null);
      } catch (err) {
        if (!cancelled) setError(errText(err, 'Failed to load cost analytics'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function applyFilters(nextPreset = datePreset, nextTenant = filterTenantId, from = customFrom, to = customTo) {
    setPage(1);
    try {
      await loadFiltered(1, nextPreset, nextTenant, from, to);
      setError(null);
    } catch (err) {
      setError(errText(err, 'Failed to load cost analytics'));
    }
  }

  async function changePage(next: number) {
    setPage(next);
    try {
      const params = buildFilters(datePreset, filterTenantId, customFrom, customTo);
      setRuns(await apiClient.getCostRuns({ ...params, page: String(next), pageSize: '25' }));
    } catch (err) {
      setError(errText(err, 'Failed to load runs'));
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Cost Analytics</h1>
        <p className="mt-1 text-sm text-gray-500">
          Monitor AI token consumption and spending across all tenants, workspaces, and agents.
        </p>
      </div>
      {error ? (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</div>
      ) : null}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-sm text-gray-500">Loading cost analytics...</div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-6">
            <Card><CardContent className="p-3 pt-3"><div className="text-xs text-gray-500">Tokens today (in / out)</div><div className="text-lg font-bold">{fmtNum(todayTokensIn)} / {fmtNum(todayTokensOut)}</div></CardContent></Card>
            <Card><CardContent className="p-3 pt-3"><div className="text-xs text-gray-500">Tokens MTD (in / out)</div><div className="text-lg font-bold">{fmtNum(mtdTokensIn)} / {fmtNum(mtdTokensOut)}</div></CardContent></Card>
            <Card><CardContent className="p-3 pt-3"><div className="text-xs text-gray-500">Time today</div><div className="text-lg font-bold">{fmtDuration(todayElapsedMs)}</div></CardContent></Card>
            <Card><CardContent className="p-3 pt-3"><div className="text-xs text-gray-500">Cost today / MTD</div><div className="text-lg font-bold">${fmtCents(overview?.todayCostCents ?? 0)} / ${fmtCents(overview?.mtdCostCents ?? 0)}</div></CardContent></Card>
            <Card><CardContent className="p-3 pt-3"><div className="text-xs text-gray-500">All-time cost</div><div className="text-lg font-bold">${fmtCents(overview?.allTimeCostCents ?? 0)}</div></CardContent></Card>
            <Card><CardContent className="p-3 pt-3"><div className="text-xs text-gray-500">Active agents / runs today</div><div className="text-lg font-bold">{overview?.activeAgentCount ?? 0} / {overview?.runCountToday ?? 0}</div></CardContent></Card>
          </div>
          <p className="mb-6 text-xs text-gray-500">
            Cursor rates from published docs{' '}
            {overview?.pricing?.fetchedAt
              ? `(refreshed ${formatWhen(overview.pricing.fetchedAt, timezone, dateFormat)}, ${overview.pricing.modelCount} models).`
              : '(not refreshed yet).'}{' '}
            {(overview?.usageRowCount ?? 0) === 0
              ? 'No Cursor usage recorded yet. Costs stay at $0 until a Collaborate run writes the ledger.'
              : null}
          </p>
          <Card className="mb-6">
            <CardContent className="p-3 pt-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="grid gap-1 text-sm">
                  <span className="text-xs text-gray-500">Tenant</span>
                  <select
                    className={selectClass}
                    value={filterTenantId}
                    onChange={(e) => {
                      setFilterTenantId(e.target.value);
                      void applyFilters(datePreset, e.target.value);
                    }}
                  >
                    <option value="">All tenants</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-xs text-gray-500">Date range</span>
                  <select
                    className={selectClass}
                    value={datePreset}
                    onChange={(e) => {
                      const next = e.target.value as DatePreset;
                      setDatePreset(next);
                      void applyFilters(next, filterTenantId);
                    }}
                  >
                    <option value="today">Today</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="month">This month</option>
                    <option value="all">All time</option>
                  </select>
                </label>
                {datePreset === 'all' ? (
                  <>
                    <label className="grid gap-1 text-sm">
                      <span className="text-xs text-gray-500">From</span>
                      <Input type="date" value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); void applyFilters(datePreset, filterTenantId, e.target.value, customTo); }} />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span className="text-xs text-gray-500">To</span>
                      <Input type="date" value={customTo} onChange={(e) => { setCustomTo(e.target.value); void applyFilters(datePreset, filterTenantId, customFrom, e.target.value); }} />
                    </label>
                  </>
                ) : null}
              </div>
            </CardContent>
          </Card>
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardContent className="p-4 pt-4">
                <h2 className="mb-1 text-base font-semibold">Daily token consumption</h2>
                <p className="mb-2 text-xs text-gray-500">Stacked bars: tokens in (blue) + tokens out (green). Cost is in the tooltip.</p>
                <BarChart points={timeseries} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 pt-4">
                <h2 className="mb-1 text-base font-semibold">Daily runs and execution time</h2>
                <p className="mb-2 text-xs text-gray-500">Bars: run count. Tooltip includes total execution time.</p>
                <RunsChart points={timeseries} />
              </CardContent>
            </Card>
          </div>
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardContent className="p-4 pt-4" id="costs-tenant-chart">
                <h2 className="mb-1 text-base font-semibold">Tokens by tenant</h2>
                <ShareList title="Total tokens consumed per tenant" rows={tenantTokens} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 pt-4" id="costs-tenant-runs-chart">
                <h2 className="mb-1 text-base font-semibold">Runs by tenant</h2>
                <ShareList title="Total runs per tenant" rows={tenantRuns} />
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardContent className="p-4 pt-4">
              <h2 className="mb-2 text-base font-semibold">Runs</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500 dark:border-gray-800">
                      <th className="py-2 pr-2">Run</th>
                      <th className="py-2 pr-2">Tenant</th>
                      <th className="py-2 pr-2">Agent</th>
                      <th className="py-2 pr-2">Model</th>
                      <th className="py-2 pr-2 text-right">Tokens in</th>
                      <th className="py-2 pr-2 text-right">Tokens out</th>
                      <th className="py-2 pr-2 text-right">Total tokens</th>
                      <th className="py-2 pr-2 text-right">Total time</th>
                      <th className="py-2 pr-2 text-right">Cost</th>
                      <th className="py-2 pr-2">Status</th>
                      <th className="py-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.items.length === 0 ? (
                      <tr><td colSpan={11} className="py-6 text-center text-sm text-gray-500">No runs with usage recorded yet.</td></tr>
                    ) : runs.items.map((r) => (
                      <tr key={r.runId} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 pr-2 font-mono text-xs">{(r.runId || '?').slice(0, 8)}...</td>
                        <td className="py-2 pr-2">{r.tenantName}</td>
                        <td className="py-2 pr-2">{r.agentName}</td>
                        <td className="py-2 pr-2 font-mono text-xs">{r.agentModel || '-'}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">{r.tokensIn.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">{r.tokensOut.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right font-medium tabular-nums">{(r.tokensIn + r.tokensOut).toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right tabular-nums text-gray-500">{fmtDuration(r.totalElapsedMs)}</td>
                        <td className="py-2 pr-2 text-right text-gray-500">${fmtCents(r.costCents)}</td>
                        <td className="py-2 pr-2"><Badge variant={r.runStatus === 'completed' ? 'success' : 'warning'}>{r.runStatus}</Badge></td>
                        <td className="py-2 text-xs text-gray-500">{formatWhen(r.runCreatedAt, timezone, dateFormat)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {runs.total > runs.pageSize ? (
                <div className="mt-3 flex justify-center gap-2">
                  <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => void changePage(page - 1)}>Prev</Button>
                  <span className="self-center text-sm">{page} / {totalPages}</span>
                  <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => void changePage(page + 1)}>Next</Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
